import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardNote, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/empty-state";
import { Field, Input, Select } from "@/components/ui/field";
import { Loading, LoadFailure } from "@/components/query-state";
import { StatusChip } from "@/components/status";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { useRuns } from "@/lib/queries";
import { runFailed } from "@/lib/schemas";
import { formatRelative, formatStamp } from "@/lib/time";
import { useNow } from "@/lib/use-now";

type OutcomeFilter = "all" | "failed" | "ok";

const LIMITS = [50, 100, 200] as const;

export function RunsPage() {
  const now = useNow();
  const [params, setParams] = useSearchParams();
  const alertId = params.get("alertId") ?? "";
  const [draftAlertId, setDraftAlertId] = useState(alertId);
  const [outcome, setOutcome] = useState<OutcomeFilter>("all");
  const [limit, setLimit] = useState<number>(100);

  const runs = useRuns({ limit, ...(alertId === "" ? {} : { alertId }) });

  // Applied here rather than in the query string because /ops/runs filters on
  // alert and limit only. Narrowing client-side is honest as long as the page
  // says what it is narrowing FROM, which the caption below does.
  const shown = (runs.data ?? []).filter((run) =>
    outcome === "all" ? true : outcome === "failed" ? runFailed(run) : !runFailed(run),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-base font-semibold">Runs</h1>
        <p className="text-xs text-muted-foreground">One line per attempt, newest first.</p>
      </div>

      <Card>
        <CardBody>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setParams(draftAlertId.trim() === "" ? {} : { alertId: draftAlertId.trim() });
            }}
          >
            <Field
              label="Alert id"
              htmlFor="alert-id"
              className="min-w-[22rem] flex-1"
              hint="Paste an alert id, or follow a link from the overview."
            >
              <Input
                id="alert-id"
                value={draftAlertId}
                placeholder="all alerts"
                onChange={(event) => setDraftAlertId(event.target.value)}
              />
            </Field>

            <Field label="Outcome" htmlFor="outcome" className="w-40">
              <Select
                id="outcome"
                value={outcome}
                onChange={(event) => setOutcome(event.target.value as OutcomeFilter)}
              >
                <option value="all">All</option>
                <option value="failed">Failures only</option>
                <option value="ok">Successes only</option>
              </Select>
            </Field>

            <Field label="Rows" htmlFor="limit" className="w-28">
              <Select
                id="limit"
                value={String(limit)}
                onChange={(event) => setLimit(Number(event.target.value))}
              >
                {LIMITS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </Field>

            <Button type="submit" size="md">
              Apply
            </Button>
            {alertId !== "" ? (
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setDraftAlertId("");
                  setParams({});
                }}
              >
                Clear alert
              </Button>
            ) : null}
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {shown.length} of {runs.data?.length ?? 0} runs
          </CardTitle>
          <CardNote>
            {alertId === "" ? "every alert" : `alert ${alertId}`} · newest {limit}
          </CardNote>
        </CardHeader>

        {runs.isPending ? <Loading what="runs" /> : null}
        {runs.error !== null ? (
          <LoadFailure what="runs" error={runs.error} onRetry={() => void runs.refetch()} />
        ) : null}

        {runs.data !== undefined && shown.length === 0 ? (
          <EmptyState title="No runs match.">
            {alertId === ""
              ? "Check that a worker is up."
              : "Nothing in this window. Check the overdue count on the overview."}
          </EmptyState>
        ) : null}

        {shown.length > 0 ? (
          <CardBody className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH>Alert</TH>
                  <TH>Outcome</TH>
                  <TH className="text-right">Listings seen</TH>
                  <TH className="text-right">New matches</TH>
                  <TH>Error kind</TH>
                </TR>
              </THead>
              <TBody>
                {shown.map((run) => (
                  <TR key={run.id}>
                    <TD data-numeric className="whitespace-nowrap">
                      <span className="block">{formatStamp(run.ranAt)}</span>
                      <span className="block text-xs text-muted-foreground">
                        {formatRelative(run.ranAt, now)}
                      </span>
                    </TD>
                    <TD>
                      <button
                        type="button"
                        className="font-mono text-xs underline underline-offset-4"
                        title={run.alertId}
                        onClick={() => {
                          setDraftAlertId(run.alertId);
                          setParams({ alertId: run.alertId });
                        }}
                      >
                        {run.alertId.slice(0, 8)}
                      </button>
                    </TD>
                    <TD>
                      <StatusChip tone={runFailed(run) ? "danger" : "ok"}>{run.outcome}</StatusChip>
                    </TD>
                    <TD data-numeric className="text-right">
                      {run.listingsSeen}
                    </TD>
                    <TD data-numeric className="text-right">
                      {run.newMatches}
                    </TD>
                    <TD className="text-muted-foreground">{run.errorKind ?? "-"}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardBody>
        ) : null}
      </Card>
    </div>
  );
}
